// useSpeechToText — Web Speech API + getUserMedia AnalyserNode für Live-Waveform
// Browser-Support: Chrome, Edge, Safari (mit webkit-Prefix). Firefox: nicht unterstützt.
// In nicht-unterstützten Browsern wird `supported` false zurückgegeben → UI blendet Mic aus.

import { useCallback, useEffect, useRef, useState } from 'react';

const isSupported = () => {
  if (typeof window === 'undefined') return false;
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  return Boolean(SR && navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
};

export default function useSpeechToText({ language = 'de-DE', onResult } = {}) {
  const [supported] = useState(isSupported);
  const [listening, setListening] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState(null);

  const recognitionRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const onResultRef = useRef(onResult);

  useEffect(() => { onResultRef.current = onResult; }, [onResult]);

  const stop = useCallback(() => {
    try { recognitionRef.current?.stop(); } catch (_) { /* noop */ }
    recognitionRef.current = null;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (_) { /* noop */ }
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    setListening(false);
    setAudioLevel(0);
  }, []);

  const start = useCallback(async () => {
    if (!supported) {
      setError('Spracherkennung wird in diesem Browser nicht unterstuetzt.');
      return;
    }
    setError(null);
    try {
      // 1) Mic-Stream für Waveform
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.7;
      source.connect(analyser);
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!analyserRef.current) return;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        setAudioLevel(Math.min(1, rms * 3.5));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();

      // 2) Speech Recognition
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      const rec = new SR();
      recognitionRef.current = rec;
      rec.lang = language;
      rec.continuous = true;
      rec.interimResults = true;
      finalTranscriptRef.current = '';

      rec.onresult = (e) => {
        let interim = '';
        let final = finalTranscriptRef.current;
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const r = e.results[i];
          if (r.isFinal) final += r[0].transcript;
          else interim += r[0].transcript;
        }
        finalTranscriptRef.current = final;
        const combined = (final + interim).trim();
        if (onResultRef.current) {
          onResultRef.current({ final: final.trim(), interim: interim.trim(), combined });
        }
      };

      rec.onerror = (e) => {
        const msg = e.error === 'not-allowed'
          ? 'Mikrofon-Zugriff verweigert. Bitte erlaube den Zugriff.'
          : e.error === 'no-speech'
            ? 'Keine Sprache erkannt — versuche es nochmal.'
            : `Fehler: ${e.error || 'unbekannt'}`;
        setError(msg);
        stop();
      };

      rec.onend = () => {
        // Bei langer Pause stoppt der Browser von alleine — wir markieren das als gestoppt
        setListening(false);
      };

      rec.start();
      setListening(true);
      if (navigator.vibrate) navigator.vibrate(15);
    } catch (e) {
      setError(e?.message || 'Mikrofon konnte nicht gestartet werden.');
      stop();
    }
  }, [supported, language, stop]);

  useEffect(() => {
    return () => stop();
  }, [stop]);

  return { supported, listening, audioLevel, error, start, stop };
}
